import { Component, Input, OnInit, OnChanges } from "@angular/core";
import { Router } from "@angular/router";
import { FormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { UIValidator } from "../../shared/validate/ui-validator.validate";

import { QuestionControlUtil } from "../../shared/utils/question-control.util";
import { VisitorControlUtil } from "../../shared/utils/visitor-control.util";

import { VisitorService } from "../../core/services/visitor.service";

import { LocalStorageUtil } from "../../shared/utils/local-storage.util";
import { VisitorAccount } from "../../shared/model/visitor-account.model";
import { SurveyAnswerService } from "../../core/services/survey-answer.service";
import { SurveyAnswerRequest } from "../../shared/model/survey-answer-request.model";
import {
  ToasterService,
  MessageType,
  InquiryResponse,
  Institution,
  SurveyQuestion,
  SurveyAnswer,
  Visitor,
} from "../../../../../shared-lib/src/public-api";
import { ThemeService } from "../../core/services/theme.service";

@Component({
  selector: "app-survey-form",
  templateUrl: "./survey-form.component.html",
  styleUrls: ["./survey-form.component.css"],
})
export class SurveyFormComponent implements OnInit, OnChanges {
  public errorMsg = "Please, complete the survey";
  public isLoading = false;
  public error: string = null;
  @Input() isEditMode = false;
  @Input() usernameSaved = "";
  @Input() institutions: Institution[] = [];
  @Input() institutionIds: number[] = [];
  @Input() questions: SurveyQuestion[] = [];
  @Input() answers: SurveyAnswer[] = [];
  public form: FormGroup;
  public submitted = false;
  public username = "";

  constructor(
    private toasterService: ToasterService,
    private visitorService: VisitorService,
    private surveyAnswerService: SurveyAnswerService,
    private themeService: ThemeService,
    private visitorControlUtil: VisitorControlUtil,
    private questionControlUtil: QuestionControlUtil,
    private localStorageUtil: LocalStorageUtil,
    private router: Router
  ) {
    this.router = router;
    this.mapInstitutions = this.mapInstitutions.bind(this);
    this.saveForm = this.saveForm.bind(this);
    this.onSurveyAnswer = this.onSurveyAnswer.bind(this);
    this.onVisitorSuccess = this.onVisitorSuccess.bind(this);
    this.onSurveyAnswerSuccess = this.onSurveyAnswerSuccess.bind(this);
    this.onError = this.onError.bind(this);
  }

  private mapInstitutions(
    institutions: Institution[],
    institutionIds: number[]
  ): Institution[] {
    return institutions.filter((inst) =>
      institutionIds.find((instId) => instId === inst.id)
    );
  }

  stepAfterSavingAnswer() {
    const infoMsg = "The survey was saved successfully";
    this.toasterService.show(MessageType.INFORMATIONAL, infoMsg);
    this.router.navigate(["/guidelines"]);
  }

  private onSurveyAnswerSuccess(response: InquiryResponse<SurveyAnswer>) {
    this.isLoading = false;
    if (response.operationSuccess) {
      this.stepAfterSavingAnswer();
    } else if (response.messages.length > 0) {
      this.toasterService.show(
        response.messages[0].type,
        response.messages[0].text
      );
    } else {
      this.onError(this.errorMsg);
    }
  }

  private onSurveyAnswer(surveyAnswer: SurveyAnswerRequest) {
    let surveyAnswerObs: Observable<InquiryResponse<SurveyAnswer>>;
    surveyAnswerObs = this.surveyAnswerService.saveAnswers(surveyAnswer);
    surveyAnswerObs.subscribe(this.onSurveyAnswerSuccess, this.onError);
  }

  private onVisitorSuccess(questions: SurveyQuestion[], form: any) {
    return (response: InquiryResponse<Visitor>) => {
      if (response.operationSuccess) {
        const instAssocWith = this.mapInstitutions(
          this.institutions,
          this.institutionIds
        );
        /*
        this.localStorageUtil.saveUserInfo(
          new VisitorAccount(response.first.username, instAssocWith)
        );*/
        //this.themeService.setInstitutions(instAssocWith);
        const surveyAnswersToSave = this.questionControlUtil.toSaveAnswers(
          response.first.username,
          response.first.id,
          questions,
          form
        );
        this.onSurveyAnswer(surveyAnswersToSave);
      } else if (response.messages.length > 0) {
        this.isLoading = false;
        this.toasterService.show(
          response.messages[0].type,
          response.messages[0].text
        );
      } else {
        this.onError(this.errorMsg);
      }
    };
  }

  private onError(error) {
    this.isLoading = false;
    this.toasterService.show(MessageType.SYSTEM_ERROR, error);
  }

  private saveForm(visitor: Visitor, questions: SurveyQuestion[], form: any) {
    this.isLoading = true;
    let visitorObs: Observable<InquiryResponse<Visitor>>;
    visitorObs = this.visitorService.saveVisitor(visitor);
    visitorObs.subscribe(this.onVisitorSuccess(questions, form), this.onError);
  }

  public isUsername(username: string) {
    return !UIValidator.isEmptyString(username);
  }

  public hasInstitutions(array: any[]) {
    return !UIValidator.isEmptyArray(array);
  }

  ngOnInit() {
    this.form = this.questionControlUtil.toFormGroup(
      this.questions,
      this.answers
    );
  }

  ngOnChanges() {
    this.form = this.questionControlUtil.toFormGroup(
      this.questions,
      this.answers
    );
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.valid && !this.isEditMode) {
      const hasUsername = this.isUsername(this.username);
      const hasInstitutions = this.hasInstitutions(this.institutionIds);
      if (hasUsername && hasInstitutions) {
        const visitorToSave = this.visitorControlUtil.toSaveVisitor(
          this.username,
          this.institutionIds
        );
        this.isLoading = true;
        this.saveForm(visitorToSave, this.questions, this.form.value);
      } else {
        this.toasterService.show(MessageType.VALIDATION, this.errorMsg);
        return;
      }
    } else if (this.form.valid && this.isEditMode) {
      const surveyAnswersToSave = this.questionControlUtil.toUpdateAnswers(
        this.usernameSaved,
        this.answers,
        this.questions,
        this.form.value
      );
      this.isLoading = true;
      this.onSurveyAnswer(surveyAnswersToSave);
    } else {
      this.toasterService.show(MessageType.VALIDATION, this.errorMsg);
      return;
    }
  }
}
